import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { RiskBadge, StatusBadge, ScorePill, STATUS_LABELS } from "../components/badges";
import ClaimDetailSheet from "../components/ClaimDetailSheet";
import { formatDate, formatIDR } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, AreaChart, Area, CartesianGrid, Legend } from "recharts";
import { Files, Hourglass, Stamp, UserFocus } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const RISK_COLORS = { rendah: "#10B981", sedang: "#F59E0B", tinggi: "#EF4444", kritis: "#7F1D1D" };

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-card border border-border p-5 hover:-translate-y-px transition-transform duration-200">
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <Icon size={18} className="text-muted-foreground" />
    </div>
    <p className="text-3xl font-semibold tracking-tight font-mono">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const canTrend = ["admin", "supervisor", "staff"].includes(user.role);

  const load = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((err) => console.error("Gagal memuat statistik:", err));
    if (canTrend) api.get("/analytics/trends").then((r) => setTrends(r.data)).catch((err) => console.error("Gagal memuat tren:", err));
  }, [canTrend]);
  useEffect(() => { load(); }, [load]);

  if (!stats) return <div className="p-8 text-sm text-muted-foreground">Memuat dashboard...</div>;

  const riskData = ["rendah", "sedang", "tinggi", "kritis"].map((k) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), key: k, jumlah: stats.by_risk[k] || 0,
  }));

  return (
    <div className="p-8 space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard Operasional</h1>
        <p className="text-sm text-muted-foreground mt-1">Ringkasan antrean klaim dan status investigasi</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-total" icon={Files} label="Total Klaim" value={stats.total}
          sub={`Jiwa ${stats.by_type.jiwa} · Kendaraan ${stats.by_type.kendaraan}`} />
        <StatCard testId="stat-avg-score" icon={UserFocus} label="Rata-rata Skor" value={stats.avg_score} sub="Skala 0–100" />
        <StatCard testId="stat-pending-approval" icon={Stamp} label="Menunggu Approval" value={stats.by_status.menunggu_approval}
          sub="Butuh keputusan supervisor" />
        <StatCard testId="stat-in-progress" icon={Hourglass} label="Dalam Investigasi" value={stats.by_status.ditugaskan}
          sub={`Menunggu penugasan: ${stats.by_status.menunggu_penugasan}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border p-5">
          <h2 className="text-sm font-semibold mb-4">Distribusi Kategori Risiko</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={riskData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="jumlah" radius={[2, 2, 0, 0]}>
                  {riskData.map((d) => <Cell key={d.key} fill={RISK_COLORS[d.key]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-semibold">Klaim Terbaru</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-5 py-2 font-medium">No. Klaim</th>
                <th className="px-3 py-2 font-medium">Jenis</th>
                <th className="px-3 py-2 font-medium">Nilai</th>
                <th className="px-3 py-2 font-medium">Skor</th>
                <th className="px-3 py-2 font-medium">Risiko</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">Belum ada klaim. Mulai dengan Input Klaim.</td></tr>
              )}
              {stats.recent.map((c) => (
                <tr key={c.id} data-testid={`recent-claim-row-${c.claim_number}`} onClick={() => setSelectedId(c.id)}
                  className="border-b border-border/60 last:border-0 hover:bg-accent cursor-pointer transition-colors duration-150">
                  <td className="px-5 py-2.5 font-mono text-xs">{c.claim_number}</td>
                  <td className="px-3 py-2.5 capitalize">{c.claim_type}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{formatIDR(c.claim_amount)}</td>
                  <td className="px-3 py-2.5"><ScorePill score={c.score} /></td>
                  <td className="px-3 py-2.5"><RiskBadge category={c.risk_category} /></td>
                  <td className="px-3 py-2.5"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {canTrend && (
        <div className="bg-card border border-border p-5" data-testid="trend-chart">
          <h2 className="text-sm font-semibold mb-4">Tren Klaim per Bulan</h2>
          {trends.length === 0 ? (
            <p className="text-sm text-muted-foreground">Belum ada data tren</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <AreaChart data={trends} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="total" name="Total Klaim" stroke="#0F172A" fill="#0F172A" fillOpacity={0.12} strokeWidth={2} />
                  <Area type="monotone" dataKey="high_risk" name="Risiko Tinggi/Kritis" stroke="#EF4444" fill="#EF4444" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      <ClaimDetailSheet claimId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />
    </div>
  );
}
